import { createFileRoute } from "@tanstack/react-router";
import { hourlyMinimumFaq } from "@/lib/booking-minimums";
import VehiclePage, { vehicleHead, FLEET_LINKS, WHICH_VEHICLE, type VehicleConfig } from "@/components/VehiclePage";
import hero from "@/assets/suburban-suv-game-day-atlanta.webp?format=webp&quality=72&w=1400&imagetools";
import mid from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";
import exterior from "@/assets/suburban-suv-game-day-atlanta.webp?format=webp&quality=72&w=1000&imagetools";
import airportCard from "@/assets/aviation/airport-window-jet.jpg?format=webp&quality=70&w=800&imagetools";

const cfg: VehicleConfig = {
  path: "/suburban-chauffeur-service-atlanta",
  title: "Suburban SUV Chauffeur Service Atlanta | Black SUV With Driver",
  description:
    "Chauffeured black Suburban SUV in Atlanta for up to 6 (captain seats) or 7 (bench). Leather, tinted privacy windows and generous luggage space. Call (678) 431-7222.",
  serviceType: "Suburban SUV Chauffeur Service",
  eyebrow: "Our Fleet • Suburban SUV",
  h1: "Suburban SUV Chauffeur Service in Atlanta",
  intro:
    "You need a calm, private ride, whether you're heading to a meeting or landing with your family. My chauffeurs welcome you into a Suburban SUV with black leather, tinted privacy windows and generous luggage space.",
  heroImage: hero,
  heroAlt: "Black Suburban SUV chauffeur vehicle in front of the Atlanta skyline",
  quickFacts: [
    { value: "6 or 7", label: "Guests: captain seats or bench" },
    { value: "Luggage", label: "Generous space for bags" },
    { value: "Tinted", label: "Privacy windows" },
    { value: "Custom", label: "Quotes upon request" },
  ],
  introImage: exterior,
  introImageAlt: "Black Suburban SUV on an Atlanta street with the skyline behind it",
  midImage: mid,
  midAlt: "Suburban SUV interior with black leather captain seats",
  aboutTitle: "Your Space to Settle In",
  about: [
        "For your workday, the Suburban gives you a quiet, discreet cabin between meetings and client visits. For your family, it means traveling together with room for your bags.",
        "We own every vehicle in our fleet. My fingerprinted, background-checked chauffeurs drive carefully, supported by cameras and AI driver-safety technology. Kevin has driven with us for 6 years, Tony for 3.",
      ],
  featuresTitle: "Suburban SUV Features",
  features: [
    "Up to 6 passengers with captain seats, or 7 with a bench",
    "Premium black leather seating",
    "Tinted privacy windows",
    "Climate-controlled cabin",
    "Generous luggage space",
    "Passengers are welcome to bring their own cooler",
  ],
  bookingBands: [
    { title: "Check the Suburban for Your Date", body: "Send the date, pickup and number of guests. We reply with a written quote." },
    { title: "Reserve the Suburban", body: "Every quote is custom and in writing before you book." },
  ],
  bestFor: ["Airport transfers", "Executive point-to-point", "Private clients", "Small families", "Private jet pickups", "Game days"],
  details: [
    {
      title: "Airport Runs, With Room for the Bags",
      body: [
        "The Suburban is a popular choice for airport transfers: up to 6 or 7 guests and generous luggage space, without the size of a Sprinter.",
        "Send your flight details, pickup address and number of bags when you book. Track your chauffeur live in our app, so you always know when they'll pull up.",
      ],
      list: [
        "Hartsfield-Jackson Atlanta International (ATL)",
        "Private terminals at PDK, Fulton County and Cobb County",
        "Hotels, homes and offices across metro Atlanta",
      ],
    },
    WHICH_VEHICLE,
  ],
  gallery: [
    { src: exterior, alt: "Black Suburban SUV in Atlanta", caption: "Discreet black exterior" },
    { src: mid, alt: "Suburban SUV leather captain seats", caption: "Black leather captain seats" },
  ],
  faqs: [
    { q: "How many people fit in the Suburban?", a: "Up to 6 passengers with captain seats, or up to 7 with bench seats." },
    { q: "Is the Suburban good for airport pickups with luggage?", a: "Yes. It has generous luggage space, which makes it a popular choice for ATL and private airport transfers." },
    { q: "Is there a cooler in the Suburban?", a: "No built-in cooler. Passengers are welcome to bring their own. Our Sprinters have built-in coolers." },
    { q: "How much does the Suburban cost?", a: "Custom quotes are given upon request based on your date and trip." },
    hourlyMinimumFaq({ transfers: true }),
  ],
  related: [FLEET_LINKS.jet, FLEET_LINKS.executive, FLEET_LINKS.limo, { to: "/atlanta-airport-car-service", title: "Atlanta Airport Car Service", desc: "Chauffeured pickups at Hartsfield-Jackson.", image: airportCard }],
  ctaTitle: "Text Me Your Suburban Plans",
  ctaBody: "Send me your date, pickup, number of guests and bags. I'll check the details personally and send your custom quote in writing.",
};

export const Route = createFileRoute("/suburban-chauffeur-service-atlanta")({
  head: () => vehicleHead(cfg),
  component: SuburbanPage,
});

function SuburbanPage() {
  return <VehiclePage cfg={cfg} />;
}
